// [목적] GET /festivals 목록 조회 훅 — useQuery + zod 파싱 + queryKey 일원화(usePoiCongestion 패턴)
// [이유] fe-home-festivals-live — HomePage "강원 축제" 섹션이 mock 대신 실 API 를 쓰도록 배선.
//        축제 목록은 자주 바뀌지 않으므로(야간 배치 동기화) staleTime 을 넉넉히(5분) 잡아 재요청을 줄인다.
// [사이드 이팩트] listFestivals → fetchWithEnvelope 경유 → 401 single-flight refresh 자동 처리(단, 이 엔드포인트는 public).
//                 zod parse 실패 시 ZodError 가 useQuery error 로 bubbling → 호출부(HomePage)가 섹션 자체를 숨김.
// [수정 시 고려사항] limit 변경 시 queryKeys.festivals.list(limit) 키가 달라져 별도 캐시 엔트리 생성됨.

import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../api/http.js';
import { queryKeys } from '../api/queryKeys.js';
import { getFestival, listFestivals, type Festival } from '../api/festivals.client.js';

/**
 * [목적] GET /festivals 래핑 훅
 * [이유] HomePage 히어로(첫 항목) + 카드 3개(다음 항목) 렌더링에 필요한 목록을 단일 쿼리로 제공.
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] 에러/빈 배열 시 호출부가 섹션을 숨기도록 처리해야 함(홈이 죽지 않게 — throwOnError:false).
 */
export function useFestivals(limit = 12) {
  return useQuery<Festival[]>({
    queryKey: queryKeys.festivals.list(limit),
    queryFn: () => listFestivals(limit),
    staleTime: 1000 * 60 * 5, // 5분 — 야간 배치 동기화 데이터라 자주 바뀌지 않음
    throwOnError: false,
  });
}

/**
 * [목적] GET /festivals/:id 래핑 훅 — 축제 상세 화면의 단건 조회
 * [이유] 목록 캐시 find 우회를 제거하고 진입 경로(목록 클릭/직접 URL/새로고침)와 무관하게 동일하게 동작시키기 위함.
 *        전역 QueryClient 기본이 retry:1(main.tsx)이라 그대로 두면 404(미존재)도 1회 재요청되어 "상세 API 1회 호출"이 깨지므로
 *        404 는 재시도하지 않도록 retry 를 명시적으로 재정의한다(그 외 오류는 전역 기본과 동일하게 1회 재시도).
 * [사이드 이팩트] id 가 비어 있으면(enabled:false) 요청하지 않음. 목록→상세 진입은 목록 캐시를 재사용하지 않으므로
 *                 항상 상세 API 를 1회 요청한다(placeholderData 미사용 — v1 결정).
 * [수정 시 고려사항] 호출부는 error instanceof ApiError && error.status === 404 로 미존재를 판별해야 함.
 *                   재시도 횟수 정책을 바꿀 때는 404 non-retry 를 반드시 유지할 것.
 */
export function useFestival(id: string | undefined) {
  return useQuery<Festival>({
    queryKey: queryKeys.festivals.detail(id ?? ''),
    queryFn: () => getFestival(id as string),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5분 — 목록과 동일(BE 캐시 TTL 300s)
    throwOnError: false,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 404) && failureCount < 1,
  });
}
